import React, { useState, useEffect, useCallback, useMemo } from 'react';
import client from '../api/client';
import { useToast } from '../context/ToastContext';
import { usePermissions, PAGE_PERMISSION_IDS } from '../hooks/usePermissions';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import CitySearchSelect from '../components/CitySearchSelect';
import TaskSearchSelect from '../components/TaskSearchSelect';
import GlassButton from '../components/GlassButton';
import {
  getTaskName,
  filterTasksByQuery,
  filterTasksByTypeStage,
  filterDependencies,
  formatScheduleDate,
  composerDisableReason,
} from '../utils/taskSearchSelect';
import { sortCitiesByName } from '../utils/citySearchSelect';

const SCHEDULE_TYPES = {
  document: { name: 'Тех. заказчик', color: '#7b9eb8' },
  hr: { name: 'HR', color: '#a99bc4' },
  procurement: { name: 'Закупки', color: '#d4b896' },
  construction: { name: 'Строительство', color: '#8bc49a' },
  marketing: { name: 'Маркетинг', color: '#d4a0b8' },
  preconstruction: { name: 'До начала строительства', color: '#7baeb8' },
  tz_construction: { name: 'Строительство (ТЗ)', color: '#8bc49a' },
};

const TYPE_LABELS = Object.fromEntries(
  Object.entries(SCHEDULE_TYPES).map(([k, v]) => [k, v.name])
);

const LINK_TYPES = {
  FS: { name: 'FS', desc: 'После окончания (Finish-to-Start)' },
  SS: { name: 'SS', desc: 'Одновременное начало (Start-to-Start)' },
  FF: { name: 'FF', desc: 'Одновременное окончание (Finish-to-Finish)' },
  SF: { name: 'SF', desc: 'Начало влияет на окончание (Start-to-Finish)' },
};

const DependencyManager = () => {
  const { showSuccess, showError } = useToast();
  const { canEdit } = usePermissions();
  const dependenciesCanEdit = canEdit(PAGE_PERMISSION_IDS.dependencies);

  const [schedules, setSchedules] = useState([]);
  const [cities, setCities] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedType, setSelectedType] = useState('');
  const [selectedStage, setSelectedStage] = useState('');

  const [fromId, setFromId] = useState(null);
  const [toId, setToId] = useState(null);
  const [linkType, setLinkType] = useState('FS');
  const [lagDays, setLagDays] = useState(0);
  const [creating, setCreating] = useState(false);
  const [slotsHint, setSlotsHint] = useState('');

  const [taskQuery, setTaskQuery] = useState('');
  const [depQuery, setDepQuery] = useState('');
  const [mobileTab, setMobileTab] = useState('tasks');

  const [criticalPath, setCriticalPath] = useState([]);
  const [showCriticalPath, setShowCriticalPath] = useState(false);

  const [editingDepId, setEditingDepId] = useState(null);
  const [editLinkType, setEditLinkType] = useState('FS');
  const [editLagDays, setEditLagDays] = useState(0);
  const [savingDep, setSavingDep] = useState(false);

  const sortedCities = useMemo(() => sortCitiesByName(cities), [cities]);

  const fetchBaseData = useCallback(async () => {
    setLoading(true);
    try {
      const citiesRes = await client.get('/cities');
      setCities(citiesRes.data || []);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      showError(getApiErrorMessage(error, 'Ошибка загрузки данных'));
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const fetchCityData = useCallback(async (cityId) => {
    if (!cityId) {
      setSchedules([]);
      setDependencies([]);
      return;
    }
    try {
      const [schedulesRes, depsRes] = await Promise.all([
        client.get('/schedules', { params: { city_id: cityId } }),
        client.get('/dependencies/task-dependencies', { params: { city_id: cityId } }),
      ]);
      setSchedules(schedulesRes.data || []);
      setDependencies(depsRes.data || []);
    } catch (error) {
      console.error('Ошибка загрузки данных объекта:', error);
      showError(getApiErrorMessage(error, 'Ошибка загрузки данных объекта'));
    }
  }, [showError]);

  useEffect(() => {
    fetchBaseData();
  }, [fetchBaseData]);

  useEffect(() => {
    fetchCityData(selectedCity);
  }, [selectedCity, fetchCityData]);

  useEffect(() => {
    setFromId(null);
    setToId(null);
    setTaskQuery('');
    setDepQuery('');
    setSelectedType('');
    setSelectedStage('');
    setSlotsHint('');
    setEditingDepId(null);
  }, [selectedCity]);

  const fetchCriticalPath = useCallback(async () => {
    try {
      const params = {};
      if (selectedCity) params.city_id = selectedCity;
      if (selectedType) params.schedule_type = selectedType;
      const response = await client.get('/dependencies/dependency-graph', { params });
      setCriticalPath(response.data.critical_path || []);
    } catch (error) {
      console.error('Ошибка расчёта критического пути:', error);
      showError(getApiErrorMessage(error, 'Ошибка расчёта критического пути'));
    }
  }, [selectedCity, selectedType, showError]);

  useEffect(() => {
    if (showCriticalPath && selectedCity) {
      fetchCriticalPath();
    }
  }, [showCriticalPath, selectedCity, selectedType, dependencies, fetchCriticalPath]);

  const stageOptions = useMemo(
    () => [...new Set(schedules.map((s) => s.construction_stage).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b, 'ru', { sensitivity: 'base' })
    ),
    [schedules]
  );

  const typeFiltered = useMemo(
    () => filterTasksByTypeStage(schedules, { type: selectedType, stage: selectedStage }),
    [schedules, selectedType, selectedStage]
  );

  const filteredSchedules = useMemo(() => {
    const searched = filterTasksByQuery(typeFiltered, taskQuery);
    return [...searched].sort((a, b) => {
      const ta = a.planned_start_date ? new Date(a.planned_start_date).getTime() : Number.POSITIVE_INFINITY;
      const tb = b.planned_start_date ? new Date(b.planned_start_date).getTime() : Number.POSITIVE_INFINITY;
      if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
      if (Number.isNaN(ta)) return 1;
      if (Number.isNaN(tb)) return -1;
      return ta - tb;
    });
  }, [typeFiltered, taskQuery]);

  const filteredDeps = useMemo(
    () =>
      filterDependencies(dependencies, {
        type: selectedType,
        stage: selectedStage,
        query: depQuery,
      }),
    [dependencies, selectedType, selectedStage, depQuery]
  );

  const disableReason = useMemo(
    () => composerDisableReason({ fromId, toId, dependencies }),
    [fromId, toId, dependencies]
  );

  const clearComposer = () => {
    setFromId(null);
    setToId(null);
    setLinkType('FS');
    setLagDays(0);
    setSlotsHint('');
  };

  const createDependency = async () => {
    if (disableReason || creating) return;
    setCreating(true);
    try {
      await client.post('/dependencies/task-dependencies', {
        predecessor_id: Number(fromId),
        successor_id: Number(toId),
        link_type: linkType,
        lag_days: lagDays,
      });
      showSuccess('Зависимость создана');
      setToId(null);
      setSlotsHint('');
      await fetchCityData(selectedCity);
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка создания зависимости'));
    } finally {
      setCreating(false);
    }
  };

  const deleteDependency = async (depId) => {
    if (!window.confirm('Удалить эту связь?')) return;
    try {
      await client.delete(`/dependencies/task-dependencies/${depId}`);
      showSuccess('Зависимость удалена');
      if (editingDepId === depId) setEditingDepId(null);
      await fetchCityData(selectedCity);
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка удаления зависимости'));
    }
  };

  const startEditDep = (dep) => {
    setEditingDepId(dep.id);
    setEditLinkType(dep.link_type || 'FS');
    setEditLagDays(dep.lag_days || 0);
  };

  const saveDepEdit = async (depId) => {
    if (savingDep) return;
    setSavingDep(true);
    try {
      await client.put(`/dependencies/task-dependencies/${depId}`, {
        link_type: editLinkType,
        lag_days: editLagDays,
      });
      showSuccess('Связь обновлена');
      setEditingDepId(null);
      await fetchCityData(selectedCity);
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка обновления зависимости'));
    } finally {
      setSavingDep(false);
    }
  };

  const fillSlotFromTask = (schedule) => {
    if (!dependenciesCanEdit) return;
    setSlotsHint('');
    if (fromId == null || fromId === '') {
      setFromId(schedule.id);
      return;
    }
    if (toId == null || toId === '') {
      if (Number(schedule.id) === Number(fromId)) {
        setSlotsHint('Задача уже выбрана как «От»');
        return;
      }
      setToId(schedule.id);
      return;
    }
    setSlotsHint('Слоты «От» и «К» заняты — очистите или замените в composer');
  };

  if (loading) {
    return (
      <div className="dep-page" data-testid="dependency-manager">
        <div className="dep-page__loading">
          <div className="loading-spinner" style={{ width: 40, height: 40 }} />
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dep-page" data-testid="dependency-manager">
      <header className="dep-page__header">
        <h1>Управление зависимостями задач</h1>
        <p className="dep-page__subtitle">
          Настройте последовательность работ для расчёта критического пути
        </p>
      </header>

      <div className="glass-panel dep-toolbar" data-testid="dep-toolbar">
        <div className="dep-toolbar__filters">
          <div className="dep-field">
            <label className="dep-field__label">Объект строительства</label>
            <CitySearchSelect
              cities={sortedCities}
              value={selectedCity}
              onChange={(id) => setSelectedCity(id)}
              placeholder="Выберите объект"
              testIdPrefix="dep-city"
              className="dep-city-select"
            />
          </div>

          {selectedCity && (
            <>
              <div className="dep-field">
                <label className="dep-field__label">Отдел</label>
                <select
                  className="form-control dep-select"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  data-testid="dep-filter-type"
                >
                  <option value="">Все отделы</option>
                  {Object.entries(SCHEDULE_TYPES).map(([key, val]) => (
                    <option key={key} value={key}>{val.name}</option>
                  ))}
                </select>
              </div>
              <div className="dep-field">
                <label className="dep-field__label">Этап</label>
                <select
                  className="form-control dep-select"
                  value={selectedStage}
                  onChange={(e) => setSelectedStage(e.target.value)}
                  data-testid="dep-filter-stage"
                >
                  <option value="">Все этапы</option>
                  {stageOptions.map((stage) => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        <div className="dep-toolbar__actions">
          <GlassButton
            variant="danger"
            size="sm"
            active={showCriticalPath}
            disabled={!selectedCity}
            onClick={() => setShowCriticalPath((v) => !v)}
            label={showCriticalPath ? 'Скрыть КП' : 'Критический путь'}
          />
        </div>
      </div>

      {showCriticalPath && selectedCity && criticalPath.length > 0 && (
        <div className="glass-panel dep-critical" data-testid="dep-critical-path">
          <strong>Критический путь:</strong> {criticalPath.length} задач.
          Задержка любой из них сдвинет срок завершения проекта.
        </div>
      )}

      {!selectedCity && (
        <div className="glass-panel dep-empty-city" data-testid="dep-pick-city">
          <h3>Выберите объект строительства</h3>
          <p>Зависимости настраиваются отдельно для каждого объекта.</p>
        </div>
      )}

      {selectedCity && dependenciesCanEdit && (
        <div className="glass-panel dep-composer" data-testid="dep-composer">
          <div className="dep-composer__row">
            <div className="dep-field dep-composer__slot">
              <label className="dep-field__label">От (предшественник)</label>
              <TaskSearchSelect
                tasks={typeFiltered}
                value={fromId}
                onChange={setFromId}
                excludeId={toId}
                typeLabels={TYPE_LABELS}
                placeholder="Поиск задачи…"
                testIdPrefix="dep-from"
              />
            </div>

            <div className="dep-composer__arrow" aria-hidden>→</div>

            <div className="dep-field dep-composer__slot">
              <label className="dep-field__label">К (последователь)</label>
              <TaskSearchSelect
                tasks={typeFiltered}
                value={toId}
                onChange={setToId}
                excludeId={fromId}
                typeLabels={TYPE_LABELS}
                placeholder="Поиск задачи…"
                testIdPrefix="dep-to"
              />
            </div>

            <div className="dep-field">
              <label className="dep-field__label" title={LINK_TYPES[linkType]?.desc}>
                Тип
              </label>
              <select
                className="form-control dep-select dep-select--narrow"
                value={linkType}
                onChange={(e) => setLinkType(e.target.value)}
                title={Object.values(LINK_TYPES).map((l) => `${l.name}: ${l.desc}`).join('\n')}
                data-testid="dep-link-type"
              >
                {Object.entries(LINK_TYPES).map(([key, val]) => (
                  <option key={key} value={key} title={val.desc}>
                    {val.name} — {val.desc}
                  </option>
                ))}
              </select>
            </div>

            <div className="dep-field">
              <label className="dep-field__label">Lag (дни)</label>
              <input
                type="number"
                className="form-control dep-select dep-select--lag"
                value={lagDays}
                onChange={(e) => setLagDays(parseInt(e.target.value, 10) || 0)}
                data-testid="dep-lag-days"
              />
            </div>

            <div className="dep-composer__buttons">
              <GlassButton
                variant="primary"
                size="sm"
                disabled={Boolean(disableReason) || creating}
                onClick={createDependency}
                label={creating ? 'Создание…' : 'Создать'}
                title={disableReason || 'Создать связь'}
                className="dep-create-btn"
                dataTestId="dep-create-link"
              />
              <GlassButton
                variant="muted"
                size="sm"
                onClick={clearComposer}
                label="Очистить"
              />
            </div>
          </div>
          <div className="dep-composer__status" data-testid="dep-composer-status">
            {slotsHint || (disableReason ? disableReason : 'Готово к созданию связи')}
          </div>
        </div>
      )}

      {selectedCity && (
        <>
          <div className="dep-mobile-tabs" data-testid="dep-mobile-tabs">
            <button
              type="button"
              className={`dep-mobile-tabs__btn${mobileTab === 'tasks' ? ' is-active' : ''}`}
              onClick={() => setMobileTab('tasks')}
            >
              Задачи ({filteredSchedules.length})
            </button>
            <button
              type="button"
              className={`dep-mobile-tabs__btn${mobileTab === 'deps' ? ' is-active' : ''}`}
              onClick={() => setMobileTab('deps')}
            >
              Связи ({filteredDeps.length})
            </button>
          </div>

          <div className="dep-columns">
            <section
              className={`glass-panel dep-column${mobileTab === 'tasks' ? ' is-mobile-visible' : ''}`}
              data-testid="dep-tasks-panel"
            >
              <div className="dep-column__head">
                <h3>Задачи ({filteredSchedules.length})</h3>
                <input
                  type="search"
                  className="form-control dep-search"
                  placeholder="Поиск по названию…"
                  value={taskQuery}
                  onChange={(e) => setTaskQuery(e.target.value)}
                  data-testid="dep-tasks-search"
                />
              </div>

              <div className="dep-list">
                {filteredSchedules.length === 0 ? (
                  <div className="dep-list__empty">Нет задач по текущим фильтрам</div>
                ) : (
                  filteredSchedules.map((schedule) => {
                    const type = SCHEDULE_TYPES[schedule.schedule_type];
                    const isCritical = showCriticalPath && criticalPath.includes(schedule.id);
                    const isFrom = Number(fromId) === Number(schedule.id);
                    const isTo = Number(toId) === Number(schedule.id);
                    const predecessorCount = dependencies.filter(
                      (d) => d.successor_id === schedule.id
                    ).length;
                    const successorCount = dependencies.filter(
                      (d) => d.predecessor_id === schedule.id
                    ).length;

                    return (
                      <button
                        type="button"
                        key={schedule.id}
                        className={[
                          'dep-task-row',
                          isFrom ? 'dep-task-row--from' : '',
                          isTo ? 'dep-task-row--to' : '',
                          isCritical ? 'dep-task-row--critical' : '',
                        ].filter(Boolean).join(' ')}
                        data-testid={`dep-task-row-${schedule.id}`}
                        onClick={() => fillSlotFromTask(schedule)}
                        disabled={!dependenciesCanEdit}
                      >
                        <div className="dep-task-row__main">
                          <span className="dep-task-row__name">{getTaskName(schedule)}</span>
                          <span
                            className="dep-task-row__type"
                            style={{ color: type?.color || 'var(--text-muted)' }}
                          >
                            {type?.name || schedule.schedule_type}
                          </span>
                        </div>
                        <div className="dep-task-row__meta">
                          <span>{schedule.construction_stage || 'Без этапа'}</span>
                          <span>
                            {formatScheduleDate(schedule.planned_start_date)}
                            {' — '}
                            {formatScheduleDate(schedule.planned_end_date)}
                          </span>
                          {(predecessorCount > 0 || successorCount > 0) && (
                            <span className="dep-task-row__counts">
                              {predecessorCount > 0 ? `←${predecessorCount}` : ''}
                              {predecessorCount > 0 && successorCount > 0 ? ' · ' : ''}
                              {successorCount > 0 ? `${successorCount}→` : ''}
                            </span>
                          )}
                        </div>
                        {(isFrom || isTo) && (
                          <span className="dep-task-row__badge">
                            {isFrom ? 'От' : 'К'}
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </section>

            <section
              className={`glass-panel dep-column${mobileTab === 'deps' ? ' is-mobile-visible' : ''}`}
              data-testid="dep-links-panel"
            >
              <div className="dep-column__head">
                <h3>Связи ({filteredDeps.length})</h3>
                <input
                  type="search"
                  className="form-control dep-search"
                  placeholder="Поиск по связям…"
                  value={depQuery}
                  onChange={(e) => setDepQuery(e.target.value)}
                  data-testid="dep-links-search"
                />
              </div>

              <div className="dep-list">
                {filteredDeps.length === 0 ? (
                  <div className="dep-list__empty">
                    {dependencies.length === 0
                      ? 'Нет связей — выберите задачи в composer выше'
                      : 'Нет связей по текущим фильтрам'}
                  </div>
                ) : (
                  filteredDeps.map((dep) => {
                    const isEditing = editingDepId === dep.id;
                    const predType = SCHEDULE_TYPES[dep.predecessor_type];
                    const succType = SCHEDULE_TYPES[dep.successor_type];

                    return (
                      <div
                        key={dep.id}
                        className="dep-link-card"
                        data-testid={`dep-link-${dep.id}`}
                      >
                        <div className="dep-link-card__names">
                          <div>
                            <span
                              className="dep-link-card__type-dot"
                              style={{ background: predType?.color || '#94a3b8' }}
                            />
                            <strong>{dep.predecessor_name || 'Неизвестно'}</strong>
                            <span className="dep-link-card__stage">
                              {dep.predecessor_stage || 'Без этапа'}
                            </span>
                          </div>
                          <div className="dep-link-card__arrow">→</div>
                          <div>
                            <span
                              className="dep-link-card__type-dot"
                              style={{ background: succType?.color || '#94a3b8' }}
                            />
                            <strong>{dep.successor_name || 'Неизвестно'}</strong>
                            <span className="dep-link-card__stage">
                              {dep.successor_stage || 'Без этапа'}
                            </span>
                          </div>
                        </div>

                        {isEditing ? (
                          <div className="dep-link-card__edit">
                            <select
                              className="form-control dep-select dep-select--narrow"
                              value={editLinkType}
                              onChange={(e) => setEditLinkType(e.target.value)}
                              data-testid={`dep-link-edit-type-${dep.id}`}
                            >
                              {Object.entries(LINK_TYPES).map(([key, val]) => (
                                <option key={key} value={key}>{val.name}</option>
                              ))}
                            </select>
                            <input
                              type="number"
                              className="form-control dep-select dep-select--lag"
                              value={editLagDays}
                              onChange={(e) =>
                                setEditLagDays(parseInt(e.target.value, 10) || 0)
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveDepEdit(dep.id);
                              }}
                              data-testid={`dep-link-edit-lag-${dep.id}`}
                            />
                            <GlassButton
                              variant="success"
                              size="sm"
                              disabled={savingDep}
                              onClick={() => saveDepEdit(dep.id)}
                              label="Сохранить"
                            />
                            <GlassButton
                              variant="muted"
                              size="sm"
                              onClick={() => setEditingDepId(null)}
                              label="Отмена"
                            />
                          </div>
                        ) : (
                          <div className="dep-link-card__meta">
                            <span className="dep-link-card__pill" title={LINK_TYPES[dep.link_type]?.desc}>
                              {dep.link_type}
                              {dep.lag_days
                                ? ` · ${dep.lag_days > 0 ? '+' : ''}${dep.lag_days}д`
                                : ''}
                            </span>
                            {dependenciesCanEdit && (
                              <div className="dep-link-card__actions">
                                <GlassButton
                                  variant="muted"
                                  size="xs"
                                  onClick={() => startEditDep(dep)}
                                  label="Изменить"
                                />
                                <GlassButton
                                  variant="danger"
                                  size="xs"
                                  onClick={() => deleteDependency(dep.id)}
                                  label="Удалить"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default DependencyManager;
